// services/reviewCommentsAndActionsService.ts

import {
  GET_PROJECT_COMMENTS_URL,
  ADD_PROJECT_COMMENT_URL,
  UPDATE_PROJECT_COMMENT_URL,
  DELETE_PROJECT_COMMENT_URL,
  GET_PROJECT_ACTIONS_URL,
  ADD_PROJECT_ACTION_URL,
  UPDATE_PROJECT_ACTION_URL,
  DELETE_PROJECT_ACTION_URL,
} from "../config/constants";
import { requestNotificationsRefresh } from "./notificationsApi";
import { apiFetch } from "../utils/apiClient";





const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");

  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const normalizedToken = rawToken
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  return `${tokenType} ${normalizedToken}`;
};

const getHeaders = () => ({
  Authorization: getAuthHeader(),
  "Content-Type": "application/json",
  Accept: "application/json",
});

const extractErrorMessage = async (
  response,
  fallbackMessage
)=> {
  try {
    const data = await response.json();

    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }

    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error;
    }
  } catch {
    // Ignore parsing failure
  }

  return fallbackMessage;
};

export const reviewCommentsAndActionsService = {
  // COMMENTS

  async getComments(projectId) {
    const response = await apiFetch(GET_PROJECT_COMMENTS_URL(projectId), {
      method: "GET",
      headers: getHeaders(),
    }, "Failed to load review comments.");

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to load review comments."
        )
      );
    }

    return await response.json();
  },

  async addComment(
    projectId,
    payload
  ) {
    const response = await apiFetch(ADD_PROJECT_COMMENT_URL(projectId), {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    }, "Failed to add review comment.");

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to add review comment."
        )
      );
    }

    // REVIEW_ADDED
    requestNotificationsRefresh();

    return await response.text();
  },

  async updateComment(
    projectId,
    commentId,
    payload
  ) {
    const response = await apiFetch(
      UPDATE_PROJECT_COMMENT_URL(projectId, commentId),
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
      "Failed to update review comment.",
    );

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to update review comment."
        )
      );
    }

    return await response.text();
  },

  async deleteComment(
    projectId,
    commentId
  ) {
    const response = await apiFetch(
      DELETE_PROJECT_COMMENT_URL(projectId, commentId),
      {
        method: "DELETE",
        headers: getHeaders(),
      },
      "Failed to delete review comment.",
    );

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to delete review comment."
        )
      );
    }

    return await response.text();
  },

  // ACTIONS

  async getActions(projectId) {
    const response = await apiFetch(GET_PROJECT_ACTIONS_URL(projectId), {
      method: "GET",
      headers: getHeaders(),
    }, "Failed to load action items.");

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to load action items."
        )
      );
    }

    return await response.json();
  },

  async addAction(
    projectId,
    payload
  ) {
    const response = await apiFetch(ADD_PROJECT_ACTION_URL(projectId), {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    }, "Failed to add action item.");

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to add action item."
        )
      );
    }

    return await response.text();
  },

  async updateAction(
    projectId,
    actionId,
    payload
  ) {
    const response = await apiFetch(
      UPDATE_PROJECT_ACTION_URL(projectId, actionId),
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
      "Failed to update action item.",
    );

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to update action item."
        )
      );
    }

    return await response.text();
  },

  async deleteAction(
    projectId,
    actionId
  ) {
    const response = await apiFetch(
      DELETE_PROJECT_ACTION_URL(projectId, actionId),
      {
        method: "DELETE",
        headers: getHeaders(),
      },
      "Failed to delete action item.",
    );

    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          "Failed to delete action item."
        )
      );
    }

    return await response.text();
  },
};